// Stateful pan/pinch/tap gesture machine for the table viewport — the mahjong
// sibling of domino's `PointerDispatcher`, trimmed down since mahjong has no
// drag-and-drop (discard is a single tap, never dragged). `InputHandler`
// forwards every pointer's raw down/move/up/cancel here; this decides what
// each pointer stream means. Pinch math itself lives in `PinchGesture.ts`
// (split out for the 200-line cap).
//
// Some screen zones are "sacred" — a pointer that goes down inside one is
// always a tap candidate, never a pan, so panning can't eat their taps and
// their taps can't drag the table: the local hand rack (discard) and the
// on-canvas buttons — reset-view and the pause gear (mirrors domino's rack +
// fit-button capture rule in `PointerDispatcher`/`main.ts`).

import type { Viewport } from '../render/Viewport.js';
import { VIEWPORT_WHEEL_ZOOM_FACTOR } from '../utils/constants.js';
import type { InteractionKind, PointerInteraction, WheelInteraction } from './InputHandler.js';
import { PinchGesture, type Point } from './PinchGesture.js';

export type { Point };

export interface ViewportControllerOptions {
  readonly viewport: Viewport;
  /** True when `point` (in screen/canvas pixels) is over the local rack. */
  readonly isInRackZone: (point: Point) => boolean;
  /** The on-canvas button under `point`, or null when there is none. A tap
   *  runs it only if the pointer is released over the button it went down on. */
  readonly buttonAt: (point: Point) => CanvasButton | null;
  /** A completed tap that started in the rack zone — the discard gesture. */
  readonly onTap: (x: number, y: number) => void;
  /** Fired after any pan/zoom mutation, so the scene can mark itself dirty. */
  readonly onViewportChange: () => void;
  /** A mouse hover position (no gesture in progress), or null once it leaves
   *  the canvas. Touch/pen never fire this — there's no "hover" there. */
  readonly onHover: (point: Point | null) => void;
}

export interface CanvasButton {
  /** Tells one button from another between press and release. */
  readonly id: string;
  readonly run: () => void;
}

type Mode = 'idle' | 'pan' | 'pinch' | 'tap';

interface PanInfo {
  readonly pointerId: number;
  lastScreen: Point;
}

export class ViewportController {
  private readonly activePointers = new Map<number, Point>();
  private readonly pinch = new PinchGesture();
  private mode: Mode = 'idle';
  private pan: PanInfo | null = null;
  private tapPointerId: number | null = null;
  /** The button the tap in progress went down on; null for a rack tap. */
  private tapButton: CanvasButton | null = null;

  constructor(private readonly opts: ViewportControllerOptions) {}

  reset(): void {
    this.activePointers.clear();
    this.mode = 'idle';
    this.pan = null;
    this.pinch.clear();
    this.tapPointerId = null;
    this.tapButton = null;
  }

  handle(event: PointerInteraction): void {
    const screen: Point = { x: event.canvasX, y: event.canvasY };
    const kind: InteractionKind = event.kind;
    const idle = this.mode === 'idle' && !this.activePointers.has(event.pointerId);
    if (kind === 'down') {
      this.handleDown(event.pointerId, screen);
    } else if (kind === 'move' && event.pointer === 'mouse' && idle) {
      this.opts.onHover(screen);
    } else if (kind === 'move') {
      this.handleMove(event.pointerId, screen);
    } else if (kind === 'up') {
      this.handleUp(event.pointerId, screen);
    } else if (kind === 'leave' && event.pointer === 'mouse' && idle) {
      this.opts.onHover(null);
    } else {
      this.handleCancel(event.pointerId);
    }
  }

  handleWheel(event: WheelInteraction): void {
    const factor = event.deltaSign === -1 ? VIEWPORT_WHEEL_ZOOM_FACTOR : 1 / VIEWPORT_WHEEL_ZOOM_FACTOR;
    this.opts.viewport.zoomAt({ x: event.canvasX, y: event.canvasY }, factor);
    this.opts.viewport.markUserControlled();
    this.opts.onViewportChange();
  }

  private handleDown(pointerId: number, screen: Point): void {
    this.activePointers.set(pointerId, screen);

    if (this.activePointers.size === 2) {
      this.startPinch();
      return;
    }
    if (this.activePointers.size > 2) return;

    const button = this.opts.buttonAt(screen);
    if (button !== null || this.opts.isInRackZone(screen)) {
      this.tapPointerId = pointerId;
      this.tapButton = button;
      this.mode = 'tap';
      return;
    }
    this.pan = { pointerId, lastScreen: screen };
    this.mode = 'pan';
  }

  private handleMove(pointerId: number, screen: Point): void {
    if (!this.activePointers.has(pointerId)) return;
    this.activePointers.set(pointerId, screen);

    if (this.mode === 'pinch' && this.pinch.isActive()) {
      this.updatePinch();
      return;
    }
    if (this.mode === 'pan' && this.pan && this.pan.pointerId === pointerId) {
      const dx = screen.x - this.pan.lastScreen.x;
      const dy = screen.y - this.pan.lastScreen.y;
      if (dx !== 0 || dy !== 0) {
        this.opts.viewport.panBy(dx, dy);
        this.opts.viewport.markUserControlled();
        this.pan.lastScreen = screen;
        this.opts.onViewportChange();
      }
    }
  }

  private handleUp(pointerId: number, screen: Point): void {
    if (!this.activePointers.has(pointerId)) return;
    this.activePointers.delete(pointerId);

    if (this.mode === 'pinch') {
      // Either finger lifting ends the pinch outright — no promotion to a
      // one-finger pan mid-gesture, which would jump the view.
      this.pinch.clear();
      this.mode = 'idle';
      return;
    }
    if (this.mode === 'tap' && this.tapPointerId === pointerId) {
      const button = this.tapButton;
      this.tapPointerId = null;
      this.tapButton = null;
      this.mode = 'idle';
      if (button === null) this.opts.onTap(screen.x, screen.y);
      // Dragging off a button before letting go is how a press is taken back.
      else if (this.opts.buttonAt(screen)?.id === button.id) button.run();
      return;
    }
    if (this.mode === 'pan' && this.pan?.pointerId === pointerId) {
      this.pan = null;
      this.mode = 'idle';
    }
  }

  private handleCancel(pointerId: number): void {
    this.activePointers.delete(pointerId);
    this.pinch.clear();
    this.pan = null;
    this.tapPointerId = null;
    this.tapButton = null;
    this.mode = 'idle';
  }

  private startPinch(): void {
    const ids = Array.from(this.activePointers.keys());
    const id1 = ids[0];
    const id2 = ids[1];
    if (id1 === undefined || id2 === undefined) return;
    const p1 = this.activePointers.get(id1);
    const p2 = this.activePointers.get(id2);
    if (p1 === undefined || p2 === undefined) return;
    this.pinch.start(this.opts.viewport, id1, id2, p1, p2);
    this.tapPointerId = null;
    this.tapButton = null;
    this.pan = null;
    this.mode = 'pinch';
  }

  private updatePinch(): void {
    const ids = this.pinch.pointerIds();
    if (!ids) return;
    const p1 = this.activePointers.get(ids[0]);
    const p2 = this.activePointers.get(ids[1]);
    if (!p1 || !p2) return;
    this.pinch.update(this.opts.viewport, p1, p2);
    this.opts.viewport.markUserControlled();
    this.opts.onViewportChange();
  }
}
